import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'

const useCursor = (key: string) => {
  const cursorsMap = useMemo(
    () => new Map<string, [string[], number]>(),
    [],
  )

  const [index, setIndex] = useState<number>(0)
  const [numCursors, setNumCursors] = useState<number>(0)

  const handleAddToIndex = useCallback(
    (delta: number) => {
      if (!cursorsMap.has(key)) {
        return
      }

      const [cursors, index] = cursorsMap.get(key)!

      const newIndex = Math.min(
        Math.max(index + delta, 0),
        cursors?.length,
      )

      cursorsMap.set(key, [cursors, newIndex])
      setIndex(newIndex)
    },
    [key, cursorsMap],
  )

  useEffect(() => {
    if (!cursorsMap.has(key)) {
      cursorsMap.set(key, [[], 0])
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [_, index] = cursorsMap.get(key)!
    setIndex(index)
  }, [key, cursorsMap])

  const add = useCallback(
    (newCursor: string | undefined) => {
      if (!cursorsMap.has(key)) {
        return
      }

      const [cursors] = cursorsMap.get(key)!

      if (!newCursor) {
        return
      }

      cursors.push(newCursor)
      setNumCursors(cursors.length)
    },
    [cursorsMap, key],
  )

  const { cursor, nextCursor } = useMemo(() => {
    if (!cursorsMap.has(key) || numCursors === 0) {
      return {}
    }

    const [cursors] = cursorsMap.get(key)!

    const cursor = cursors[index - 1]
    const nextCursor = cursors[index]

    return {
      cursor,
      nextCursor,
    }
  }, [cursorsMap, key, index, numCursors])

  return {
    cursor,
    isNext: !!nextCursor,
    isPrev: index > 0,
    next: () => handleAddToIndex(1),
    prev: () => handleAddToIndex(-1),
    add,
  }
}

export default useCursor
